import {
  Button,
  ButtonGroup,
  Card,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  NumberInput,
  RadioItem,
  Separator,
  isCurrency,
  Segment,
  DialogFooter,
  Alert,
  Tooltip,
} from 'platform/components';
import {Box, Grid, GridItem, HStack, Icon, Show, Space, Text, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';
import {Pattern, match} from 'ts-pattern';
import {ValidationError} from 'yup';

import {RefObject, useState} from 'react';
import {UseFormReturn} from 'react-hook-form';

import {defaultTo, includes, isNil, isNotNil, join, not} from 'ramda';
import {isFalse, isFalsy, isNilOrEmpty} from 'ramda-adjunct';

import {useGetManufacturersQuery} from '@dms/api/core';
import {useGetEmployeeMechanicsQuery} from '@dms/api/customer';
import {useGetOperationItemsQuery} from '@dms/api/finance';
import {
  useGetMaterialPriceTypesQuery,
  useGetSupplierDiscountGroupsQuery,
  useGetWarehouseQuery,
  useGetWarehousesQuery,
} from '@dms/api/warehouse';
import {
  useGetMetadaServiceCaseQuery,
  useGetOneTimeSaleForMaterialItemQuery,
  useGetOrderItemForMaterialQuery,
  useGetServiceOrderQuery,
  useGetServiceOrderVariantQuery,
  usePatchOrderItemForMaterialMutation,
  usePutOneTimeSaleForMaterialItemMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {
  DEFAULT_CURRENCY,
  getDecimalFromPercentage,
  getOptionsFromManufacturers,
  getPercentageFromDecimal,
  handleApiError,
  useGetVatRatesOptions,
  usePermissions,
} from '@dms/shared';

import {
  TestIdProps,
  composePath,
  parseDate,
  precisionCalculation,
  suffixTestId,
  yupNumber,
} from 'shared/web';

import {getOptionsFromMaterialPriceTypes} from '../../../utils/getOptionsFromMaterialPriceTypes';
import {ItemPrice} from '../../ItemPrice/ItemPrice';
import {EditMaterialItemFormType, getFormSchema} from '../types/EditMaterialItemFormType';
import {AvailabilityTab} from './AvailabilityTab';
import {RequestsTab} from './RequestsTab';

enum ListSegment {
  availability = 'availability',
  requests = 'requests',
}

interface EditMaterialItemFormProps extends TestIdProps {
  itemId: string;
  serviceCaseId: string;
  serviceOrderId: string;
  serviceJobId: string;
  onClose: VoidFunction;
  serviceOrderIssueNoteId?: string;
  isSavedByRowControlRef?: RefObject<boolean>;
  formRef?: RefObject<{
    submit: () => Promise<boolean>;
    isDirty: boolean;
  } | null>;
}

export const EditMaterialItemForm = (props: EditMaterialItemFormProps) => {
  const formatCurrency = useFormatCurrency();

  const [listSegment, setListSegment] = useState<ListSegment>(ListSegment.availability);

  const {data: serviceCase} = useGetMetadaServiceCaseQuery({serviceCaseId: props.serviceCaseId});

  const {data: serviceOrder} = useGetServiceOrderQuery({
    serviceCaseId: props.serviceCaseId,
    serviceOrderId: props.serviceOrderId,
  });

  const {data: serviceOrderVariant, isLoading: isServiceOrderVariantLoading} =
    useGetServiceOrderVariantQuery(
      {serviceOrderVariantId: serviceOrder?.serviceOrderVariantId as string},
      {skip: isNil(serviceOrder?.serviceOrderVariantId)}
    );

  const {data, isLoading, isFetching, isError} = useGetOrderItemForMaterialQuery(
    {
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.serviceOrderId,
      serviceJobId: props.serviceJobId,
      serviceItemId: props.itemId,
    },
    {refetchOnMountOrArgChange: true}
  );
  const {
    data: oneTimeSaleData,
    isLoading: isOneTimeSaleLoading,
    isFetching: isOneTimeSaleFetching,
    isError: isOneTimeSaleError,
  } = useGetOneTimeSaleForMaterialItemQuery({
    serviceCaseId: props.serviceCaseId,
    serviceOrderId: props.serviceOrderId,
    serviceJobId: props.serviceJobId,
    serviceItemId: props.itemId,
  });

  const materialPriceTypesQuery = useGetMaterialPriceTypesQuery();

  const {data: warehouses, isLoading: isWarehousesLoading} = useGetWarehousesQuery();

  const warehouseQuery = useGetWarehouseQuery(
    {warehouseId: data?.warehouse?.warehouseId as string},
    {skip: isNilOrEmpty(data?.warehouse?.warehouseId)}
  );

  const {data: manufacturers, isLoading: isManufacturersLoading} = useGetManufacturersQuery();

  const {supplierDiscountGroupsOptions, isLoadingSupplierDiscountGroups} =
    useGetSupplierDiscountGroupsQuery(
      {
        supplierId: warehouseQuery.data?.defaultSupplierId as string,
        authorizationProfileId: warehouseQuery.data?.authorizationProfileId as string,
        discountGroupId: data?.defaultSupplierDiscountGroupId as string,
      },
      {
        skip:
          isNilOrEmpty(warehouseQuery.data?.defaultSupplierId) ||
          isNilOrEmpty(warehouseQuery.data?.authorizationProfileId) ||
          isNilOrEmpty(data?.defaultSupplierDiscountGroupId),
        selectFromResult: (result) => ({
          isLoadingSupplierDiscountGroups: result.isLoading,
          supplierDiscountGroupsOptions: isNilOrEmpty(data?.warehouse?.warehouseId)
            ? []
            : result.data?.discountGroupCode?.map((supplierDiscountGroup) => ({
                label: supplierDiscountGroup?.code,
                value: supplierDiscountGroup?.discountGroupId,
              })),
        }),
      }
    );
  const {data: order, isLoading: isOrderLoading} = useGetServiceOrderQuery({
    serviceCaseId: props.serviceCaseId,
    serviceOrderId: props.serviceOrderId,
  });
  const {data: orderVariant, isLoading: isOrderVariantLoading} = useGetServiceOrderVariantQuery(
    {serviceOrderVariantId: order?.serviceOrderVariantId ?? ''},
    {skip: isNil(order?.serviceOrderVariantId)}
  );
  const isOrderReadOnly =
    includes(order?.state, ['CLOSED', 'CANCELLED']) || includes(serviceCase?.state, ['CANCELLED']);

  const [
    canEditCustomPrice,
    canEditManualDiscount,
    canReadEmployee,
    canReadServiceOrderFinancesMaterialItem,
  ] = usePermissions({
    permissionKeys: [
      'serviceItemMaterialCustomPriceEdit',
      'serviceItemMaterialManualDiscountEdit',
      'employeeRead',
      'serviceOrderFinancesMaterialItemRead',
    ],
    scopes: {
      serviceItemMaterialCustomPriceEdit: {
        branchId: serviceCase?.branchId ?? '',
        serviceCaseType: orderVariant?.serviceCaseTypeId ?? '',
        serviceOrderType: orderVariant?.serviceOrderTypeId ?? '',
      },
      serviceItemMaterialManualDiscountEdit: {
        branchId: serviceCase?.branchId ?? '',
        serviceCaseType: orderVariant?.serviceCaseTypeId ?? '',
        serviceOrderType: orderVariant?.serviceOrderTypeId ?? '',
      },
      serviceOrderFinancesMaterialItemRead: {
        branchId: serviceCase?.branchId ?? '',
        serviceCaseType: orderVariant?.serviceCaseTypeId ?? '',
        serviceOrderType: orderVariant?.serviceOrderTypeId ?? '',
      },
    },
  });

  const {data: mechanics, isLoading: isMechanicsLoading} = useGetEmployeeMechanicsQuery(
    {
      authorizationProfileId: serviceCase?.authorizationProfileId ?? '',
      isActive: true,
      withToOffer: true,
    },
    {
      skip: isNil(serviceCase?.authorizationProfileId) || !canReadEmployee,
    }
  );
  const {
    data: operationItems,
    isLoading: isOperationItemsLoading,
    isError: isOperationItemsError,
  } = useGetOperationItemsQuery(
    {
      application: 'workshop',
      itemGroup: 'material',
    },
    {
      selectFromResult: (data) => ({
        ...data,
        data: data?.data?.map((item) => ({
          label: item.itemCodeName,
          value: item.operationId,
        })),
      }),
    }
  );

  const goodwillRatio = data?.goodwill?.goodwillRatio ?? 1;
  const hasGoodwill = goodwillRatio !== 1;

  const [patchMaterialItem] = usePatchOrderItemForMaterialMutation({
    fixedCacheKey: 'edit-service-order-item',
  });

  const [putOneTimeSaleForMaterialItem, {isLoading: isSavingOneTimeSaleChanges}] =
    usePutOneTimeSaleForMaterialItemMutation();

  const [vatOptions] = useGetVatRatesOptions({fullLabel: true});

  const handleSubmit: FormSubmitHandler<EditMaterialItemFormType> = (formData) =>
    patchMaterialItem({
      serviceCaseId: props.serviceCaseId,
      serviceItemId: props.itemId,
      serviceJobId: props.serviceJobId,
      serviceOrderId: props.serviceOrderId,
      body: {
        ...formData,
        isDiscount: !!formData.isCustomDiscount,
        sellingPriceVat: {
          freeCode: formData.vatFreeCode,
          type: formData.vatType,
        },
        mechanic: formData.mechanic ?? null,
        isDoNotApplyDiscount: !!formData.isDoNotApplyDiscount,
        discount: {
          sourceType: formData.discountType,
          source: data?.discount?.availableDiscounts?.find(
            (discount) => discount?.sourceType === formData.discountType
          )?.source,
          rate:
            (formData.discountType === 'manual-discount'
              ? getDecimalFromPercentage(formData.discountRate)
              : data?.discount?.availableDiscounts?.find(
                  (discount) => discount?.sourceType === formData.discountType
                )?.rate) ?? 0,
        },
        originEntityHeaderType: 'service-order-issue-note',
        originEntityHeaderId: props.serviceOrderIssueNoteId,
        workItemGroupId: formData.workItemGroupId ?? null,
      },
    })
      .unwrap()
      .then(() => {
        // Saving changes by rowControl change should not close dialog.
        if (props.isSavedByRowControlRef?.current) {
          props.isSavedByRowControlRef.current = false;
          return;
        }
        props.onClose();
      })
      .catch(handleApiError);

  const handleCustomPriceSwitch =
    (formApi: UseFormReturn<EditMaterialItemFormType>) => (value: boolean) => {
      if (value) {
        return;
      }

      formApi.setValue('sellingPricePerUnit', data?.baseSellingPricePerUnit?.amount ?? 0);
      formApi.setValue('vatType', data?.baseSellingPriceVat?.type ?? '');
    };

  const validateQuantity = (
    value: number | null,
    formApi: UseFormReturn<EditMaterialItemFormType>
  ) => {
    const valueToValidate = defaultTo(0, value);

    try {
      formApi.clearErrors('quantity');

      quantitySchema(dispensingUnit).validateSync(valueToValidate);

      return true;
    } catch (error) {
      if (error instanceof ValidationError) {
        formApi.setError('quantity', {message: error.errors.join(' ')});
      }

      return false;
    }
  };

  const handleDetailRedirect = () =>
    window.open(
      composePath(warehouseRoutes.articleDetailOverview, {
        params: {
          id: data?.warehouse?.warehouseArticleId,
          warehouseId: data?.warehouse?.warehouseId,
        },
        isSearchParamsPreserved: false,
      }),
      '_blank'
    );

  const dispensingUnit = defaultTo(1, data?.warehouse?.dispensingUnit);

  const currency = data?.sellingPricePerUnit?.currency;
  const warehouseOptions = warehouses?.map((warehouse) => ({
    label: warehouse.name,
    value: warehouse.id,
  }));
  const mechanicOptions =
    mechanics?.map((mechanic) => ({
      label: mechanic?.name,
      value: mechanic?.employeeId,
    })) ?? [];
  const baseVat = vatOptions.find((vat) => vat.value === data?.baseSellingPriceVat?.type);
  const basePrices = join(', ', [
    `${i18n.t('entity.order.itemPurchasePrice')}: ${formatCurrency(
      data?.baseSellingPricePerUnit?.amount ?? 0,
      data?.baseSellingPricePerUnit?.currency ?? DEFAULT_CURRENCY,
      2
    )}`,
    `${i18n.t('general.labels.vat')}: ${baseVat?.rate} %`,
  ]);

  const defaultValues: EditMaterialItemFormType = {
    sNo: null,
    catalogNumber: data?.number,
    manufacturerId: data?.warehouse?.manufacturerId,
    name: data?.name,
    warehouseId: data?.warehouse?.warehouseId,
    storageLocation: data?.warehouse?.storageLocation,
    priceType: serviceOrderVariant?.priceType,
    discountRate: getPercentageFromDecimal(
      data?.discount?.availableDiscounts?.find(
        (discount) => discount?.sourceType === 'manual-discount'
      )?.rate
    ),
    discountType:
      data?.discount?.availableDiscounts?.find((discount) => discount?.isSelected)?.sourceType ??
      'manual-discount',
    isCustomDiscount:
      data?.discount?.availableDiscounts?.some((discount) => discount?.isSelected) ?? false,
    isCustomPrice: data?.isCustomPrice ?? false,
    isUnitPriceWithVat: data?.isUnitPriceWithVat ?? false,
    mechanic: data?.assignMechanic ?? undefined,
    quantity: data?.quantity?.amount ?? 0,
    sellingPricePerUnit: data?.sellingPricePerUnit?.amount ?? 0,
    vatFreeCode: data?.sellingPriceVat?.freeCode ?? undefined,
    vatType: data?.sellingPriceVat?.type ?? 'S',
    isDoNotApplyDiscount: data?.isDoNotApplyDiscount ?? false,
    neededAt: isNotNil(data?.warehouse?.neededAt)
      ? parseDate(data?.warehouse?.neededAt)
      : undefined,
    defaultSupplierDiscountGroupId: data?.defaultSupplierDiscountGroupId,
    workItemGroupId: data?.workItemGroupId ?? undefined,
    oneTimeSale: oneTimeSaleData?.isOneTimeSale ?? false,
  };

  const segments = [
    {
      value: ListSegment.availability,
      label: i18n.t('entity.warehouse.labels.availability'),
      content: (
        <AvailabilityTab
          manufacturerNumber={data?.number}
          manufacturerId={data?.warehouse?.manufacturerId}
        />
      ),
    },
    {
      value: ListSegment.requests,
      label: i18n.t('entity.warehouse.labels.requests'),
      content: <RequestsTab itemId={props.itemId} />,
    },
  ];

  const segment = segments.find((item) => item.value === listSegment)?.content;

  return (
    <DataStatus
      isLoading={
        isLoading ||
        isFetching ||
        isLoadingSupplierDiscountGroups ||
        isWarehousesLoading ||
        isOperationItemsLoading ||
        isOneTimeSaleLoading ||
        isManufacturersLoading ||
        isServiceOrderVariantLoading ||
        materialPriceTypesQuery.isLoading
      }
      isError={isError || isOperationItemsError || isOneTimeSaleError}
      minHeight={87}
    >
      <Form<EditMaterialItemFormType>
        defaultValues={defaultValues}
        experimentalZodSchema={getFormSchema(
          data?.warehouse?.warehouseAction === 'dispatch-request'
        )}
        onSubmit={handleSubmit}
        isDisabled={isOrderReadOnly}
      >
        {(control, formApi) => {
          // Used for saving changes by rowControl in parent.
          if (props.formRef) {
            // eslint-disable-next-line react-hooks/immutability
            props.formRef.current = {
              submit: () => control.submitForm(),
              isDirty: formApi.formState.isDirty,
            };
          }

          const isTouched = formApi.formState.isDirty;

          return (
            <VStack spacing={4}>
              <Show when={hasGoodwill}>
                <Alert
                  variant="info"
                  type="inline"
                  title={i18n.t('entity.addWork.lables.itemHasGoodwill')}
                  message={i18n.t('entity.addWork.lables.goodwillRatio', {
                    ratio: goodwillRatio * 100,
                  })}
                  data-testid={suffixTestId('itemHasGoodwill', props)}
                />
              </Show>
              <Card variant="inlineGrey">
                <VStack spacing={4}>
                  <Grid spacing={4} columns={8}>
                    <GridItem span={4}>
                      <HStack spacing={4}>
                        <Box width={13}>
                          <FormField
                            isDisabled
                            control={control}
                            type="text"
                            name="sNo"
                            label={i18n.t('entity.warehouse.labels.sNo')}
                          />
                        </Box>
                        <Box width={50}>
                          <FormField
                            isDisabled
                            control={control}
                            type="text"
                            name="catalogNumber"
                            label={i18n.t('entity.warehouse.labels.catalogNumber')}
                          />
                        </Box>
                        <Box width={68}>
                          <FormField
                            isDisabled
                            control={control}
                            type="choice"
                            name="manufacturerId"
                            label={i18n.t('entity.warehouse.labels.manufacturer')}
                            options={getOptionsFromManufacturers(manufacturers)}
                          />
                        </Box>
                      </HStack>
                    </GridItem>
                    <GridItem span={4}>
                      <FormField
                        isDisabled
                        control={control}
                        type="text"
                        name="name"
                        label={i18n.t('entity.warehouse.labels.name')}
                      />
                    </GridItem>
                  </Grid>

                  <Grid spacing={4} columns={4}>
                    <GridItem span={1}>
                      <FormField
                        isDisabled
                        control={control}
                        type="choice"
                        name="warehouseId"
                        label={i18n.t('entity.warehouse.labels.warehouse')}
                        options={warehouseOptions ?? []}
                        data-testid={suffixTestId('warehouse', props)}
                      />
                    </GridItem>
                    <GridItem span={1}>
                      <FormField
                        isDisabled
                        control={control}
                        type="text"
                        name="storageLocation"
                        label={i18n.t('entity.warehouse.labels.storageLocation')}
                        data-testid={suffixTestId('storageLocation', props)}
                      />
                    </GridItem>
                    <GridItem span={1}>
                      <FormField
                        isDisabled
                        isLoading={isLoadingSupplierDiscountGroups}
                        control={control}
                        type="choice"
                        name="defaultSupplierDiscountGroupId"
                        label={i18n.t('entity.workshopCustomerGroup.labels.rabatGroup')}
                        options={supplierDiscountGroupsOptions ?? []}
                        data-testid={suffixTestId('discountGroup', props)}
                      />
                    </GridItem>
                    <GridItem span={1}>
                      <FormField
                        isDisabled
                        control={control}
                        type="choice"
                        name="priceType"
                        label={i18n.t('entity.warehouse.labels.priceType')}
                        options={getOptionsFromMaterialPriceTypes(materialPriceTypesQuery.data)}
                      />
                    </GridItem>
                  </Grid>

                  <Grid spacing={4} columns={4} align="flex-end">
                    <GridItem span={1}>
                      <FormField
                        control={control}
                        label={i18n.t('entity.warehouse.labels.neededAt')}
                        name="neededAt"
                        type="date"
                        data-testid={suffixTestId('neededAt', props)}
                      />
                    </GridItem>
                    <GridItem span={1}>
                      <FormField
                        control={control}
                        type="choice"
                        name="workItemGroupId"
                        label={i18n.t('entity.warehouse.labels.workItemGroup')}
                        options={operationItems ?? []}
                        menuInPortal
                        data-testid={suffixTestId('workItemGroup', props)}
                      />
                    </GridItem>
                    <GridItem span={1}>
                      <HStack spacing={1} align="center">
                        <VStack justify="center">
                          <FormField
                            isDisabled={
                              !oneTimeSaleData?.isEditingAllowed ||
                              isOneTimeSaleFetching ||
                              isSavingOneTimeSaleChanges
                            }
                            control={control}
                            name="oneTimeSale"
                            type="checkbox"
                            label={i18n.t('entity.warehouse.labels.oneTimeSale')}
                            onChange={(value) =>
                              putOneTimeSaleForMaterialItem({
                                serviceCaseId: props.serviceCaseId,
                                serviceOrderId: props.serviceOrderId,
                                serviceJobId: props.serviceJobId,
                                serviceItemId: props.itemId,
                                body: {
                                  isOneTimeSale: value,
                                },
                              })
                            }
                            data-testid={suffixTestId('oneTimeSale', props)}
                          />
                          <Space vertical={2} />
                        </VStack>
                        <VStack justify="center">
                          <Tooltip
                            placement="top"
                            description={i18n.t('entity.warehouse.labels.oneTimeSaleTooltip')}
                          >
                            <Icon
                              value="action/help"
                              color="text.secondary"
                              cursor="pointer"
                              size={4}
                            />
                            <Space vertical={2} />
                          </Tooltip>
                        </VStack>
                      </HStack>
                    </GridItem>
                  </Grid>

                  <Separator spacing={0} />

                  <Grid spacing={4} columns={4}>
                    <GridItem span={1}>
                      <FormField
                        control={control}
                        name="quantity"
                        type="number"
                        label={i18n.t('entity.warehouse.labels.quantity')}
                        step={dispensingUnit}
                        isStepperVisible
                        shouldRoundStepsByDifference
                        onChange={(value) => validateQuantity(value, formApi)}
                        decimalPlaces={4}
                        isDisabled={!data?.isQuantityEditingAllowed}
                        data-testid={suffixTestId('quantity', props)}
                      />
                    </GridItem>
                    <GridItem span={1}>
                      <FormField
                        control={control}
                        type="currency"
                        name="sellingPricePerUnit"
                        currency={isCurrency(currency) ? currency : undefined}
                        label={i18n.t('entity.order.itemPurchasePrice')}
                        isDisabled={
                          !formApi.watch('isCustomPrice') ||
                          isFalse(data?.sellingPricePerUnit?.isEditingAllowed) ||
                          !canEditCustomPrice
                        }
                        data-testid={suffixTestId('sellingPricePerUnit', props)}
                      />
                    </GridItem>
                    <GridItem span={1}>
                      <FormField
                        control={control}
                        type="choice"
                        name="vatType"
                        label={i18n.t('general.labels.vat')}
                        options={vatOptions}
                        isDisabled={
                          !formApi.watch('isCustomPrice') ||
                          isFalse(data?.sellingPriceVat?.isEditingAllowed) ||
                          !canEditCustomPrice
                        }
                        isNotClearable
                        menuInPortal
                        data-testid={suffixTestId('vat', props)}
                      />
                    </GridItem>
                    <GridItem span={1}>
                      <FormField
                        control={control}
                        type="choice"
                        name="mechanic"
                        options={mechanicOptions}
                        isLoading={isOrderLoading || isOrderVariantLoading || isMechanicsLoading}
                        label={i18n.t('entity.order.labels.mechanic')}
                        menuInPortal
                        data-testid={suffixTestId('mechanic', props)}
                      />
                    </GridItem>
                  </Grid>

                  <Show when={formApi.watch('isCustomPrice')}>
                    <HStack spacing={2}>
                      <Icon size={4} value="action/info" color="palettes.neutral.300.100" />
                      <Text size="xSmall" color="tertiary">
                        {`${i18n.t('entity.orderItem.labels.customPriceDescription')} ${basePrices}.`}
                      </Text>
                    </HStack>
                  </Show>
                  <Box maxWidth="fit-content">
                    <FormField
                      control={control}
                      type="switch"
                      name="isCustomPrice"
                      label={i18n.t('entity.orderItem.labels.customPrice')}
                      onChange={handleCustomPriceSwitch(formApi)}
                      isDisabled={isFalse(data?.isCustomPriceEditingAllowed) || !canEditCustomPrice}
                      data-testid={suffixTestId('isCustomPrice', props)}
                    />
                  </Box>
                  <HStack justify="space-between" align="center">
                    <FormField
                      control={control}
                      type="switch"
                      name="isCustomDiscount"
                      label={i18n.t('general.labels.discount')}
                      isDisabled={
                        isFalsy(data?.discount?.isEditingAllowed) || !canEditManualDiscount
                      }
                      data-testid={suffixTestId('discount', props)}
                    />
                    <FormField
                      control={control}
                      name="isDoNotApplyDiscount"
                      type="checkbox"
                      isDisabled={isFalsy(data?.isDoNotApplyDiscountEditable)}
                      label={i18n.t('general.labels.doNotApplyDiscount')}
                      data-testid={suffixTestId('doNotApplyDiscount', props)}
                    />
                  </HStack>
                  <Show
                    when={
                      formApi.watch('isCustomDiscount') && not(data?.discount?.isDoNotApplyDiscount)
                    }
                  >
                    <Grid columns={4}>
                      <Show
                        when={data?.discount?.availableDiscounts?.find(
                          (discount) => discount?.sourceType === 'customer-contract'
                        )}
                      >
                        <RadioItem
                          value={formApi.watch('discountType') === 'customer-contract'}
                          onChange={() =>
                            formApi.setValue('discountType', 'customer-contract', {
                              shouldDirty: true,
                            })
                          }
                          isDisabled={!canEditManualDiscount || isOrderReadOnly}
                          data-testid={suffixTestId('customer-contract', props)}
                        >
                          {i18n.t('entity.customerContract.labels.customerContract')}
                        </RadioItem>
                        <NumberInput
                          isDisabled
                          value={
                            getPercentageFromDecimal(
                              data?.discount?.availableDiscounts?.find(
                                (discount) => discount?.sourceType === 'customer-contract'
                              )?.rate ?? 0
                            ) ?? 0
                          }
                          suffix="%"
                          data-testid={suffixTestId('customer-contract', props)}
                        />
                      </Show>
                      <Show
                        when={data?.discount?.availableDiscounts?.find(
                          (discount) => discount?.sourceType === 'custom-discounts'
                        )}
                      >
                        <RadioItem
                          value={formApi.watch('discountType') === 'custom-discounts'}
                          onChange={() =>
                            formApi.setValue('discountType', 'custom-discounts', {
                              shouldDirty: true,
                            })
                          }
                          isDisabled={!canEditManualDiscount || isOrderReadOnly}
                          data-testid={suffixTestId('custom-discounts', props)}
                        >
                          {i18n.t('entity.orderItem.labels.customDiscount')}
                        </RadioItem>
                        <NumberInput
                          isDisabled
                          value={
                            getPercentageFromDecimal(
                              data?.discount?.availableDiscounts?.find(
                                (discount) => discount?.sourceType === 'custom-discounts'
                              )?.rate ?? 0
                            ) ?? 0
                          }
                          suffix="%"
                          data-testid={suffixTestId('custom-discounts', props)}
                        />
                      </Show>
                      <Show
                        when={data?.discount?.availableDiscounts?.find(
                          (discount) => discount?.sourceType === 'manual-discount'
                        )}
                      >
                        <RadioItem
                          value={formApi.watch('discountType') === 'manual-discount'}
                          isDisabled={!canEditManualDiscount || isOrderReadOnly}
                          onChange={() =>
                            formApi.setValue('discountType', 'manual-discount', {
                              shouldDirty: true,
                            })
                          }
                        >
                          {i18n.t('entity.orderItem.labels.manualDiscount')}
                        </RadioItem>
                        <FormField
                          control={control}
                          type="number"
                          name="discountRate"
                          decimalPlaces={2}
                          suffix="%"
                          maxStepperValue={100}
                          minStepperValue={0}
                          isDisabled={
                            formApi.watch('discountType') !== 'manual-discount' ||
                            !canEditManualDiscount
                          }
                          data-testid={suffixTestId('discount', props)}
                        />
                      </Show>
                    </Grid>
                  </Show>
                </VStack>
              </Card>

              <ItemPrice
                itemId={props.itemId}
                serviceCaseId={props.serviceCaseId}
                serviceOrderId={props.serviceOrderId}
                serviceJobId={props.serviceJobId}
                amount={formApi.watch('quantity')}
                purchasePricePerUnit={data?.purchasePricePerUnit?.amount}
                purchaseCurrency={data?.purchasePricePerUnit?.currency}
                sellingPricePerUnit={formApi.watch('sellingPricePerUnit')}
                sellingCurrency={data?.sellingPricePerUnit?.currency}
                vatType={formApi.watch('vatType')}
                dispensingUnit={dispensingUnit}
                formApi={formApi}
                isEditMaterialItem
                discountRate={match([
                  formApi.watch('isCustomDiscount'),
                  formApi.watch('discountType'),
                ])
                  .with([false, Pattern.any], () => 0)
                  .with([true, 'manual-discount'], () =>
                    getDecimalFromPercentage(formApi.watch('discountRate'))
                  )
                  .with([true, Pattern.any], () => {
                    const discountRate = data?.discount?.availableDiscounts?.find(
                      (discount) => discount?.sourceType === formApi.watch('discountType')
                    )?.rate;

                    return isNil(discountRate) ? 0 : discountRate;
                  })
                  .otherwise(() => 0)}
                data-testid={suffixTestId('itemPrice', props)}
                showPurchasePrice={canReadServiceOrderFinancesMaterialItem}
                showMargin={canReadServiceOrderFinancesMaterialItem}
                isDisabled={isOrderReadOnly}
              />

              <Grid columns={3}>
                <Segment<ListSegment>
                  value={listSegment}
                  onChange={setListSegment}
                  options={segments}
                />
              </Grid>
              {segment}
              <DialogFooter>
                <ButtonGroup align="right">
                  <Button
                    title={i18n.t('general.labels.discard')}
                    onClick={props.onClose}
                    variant="secondary"
                    data-testid={suffixTestId('discard', props)}
                  />
                  <Button
                    leftIcon="action/open_in_new"
                    title={i18n.t('entity.orderItem.labels.articleDetail')}
                    onClick={handleDetailRedirect}
                    variant="secondary"
                    data-testid={suffixTestId('detail', props)}
                  />
                  <FormButton
                    isDisabled={!isTouched}
                    control={control}
                    type="submit"
                    title={i18n.t('general.labels.saveChanges')}
                    data-testid={suffixTestId('submit', props)}
                  />
                </ButtonGroup>
              </DialogFooter>
            </VStack>
          );
        }}
      </Form>
    </DataStatus>
  );
};

const quantitySchema = (dispensingUnit: number) =>
  yupNumber
    .min(0.0001, (params) => i18n.t('general.errors.number.greaterOrEqual', {min: params.min}))
    .test(
      'isQuantityDivisibleByDispensingUnit',
      `${i18n.t('entity.warehouse.labels.quantityMustBeMultipleOfDispensingUnit')} (${dispensingUnit})`,
      (value) => precisionCalculation.modulo(defaultTo(0, value), dispensingUnit) === 0
    );
